"use client";
import React, { useEffect, ReactNode, useState } from "react";
import Lenis from "lenis";

interface SmoothScrollbarProps {
  children: ReactNode;
}

const SmoothScrollbar: React.FC<SmoothScrollbarProps> = ({ children }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Detect mobile device
    const checkMobile = () => {
      const mobile =
        window.matchMedia("(max-width: 768px)").matches ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        );
      setIsMobile(mobile);
      return mobile;
    };

    const mobile = checkMobile();

    // Skip Lenis on mobile for better native scroll performance
    if (mobile) {
      return;
    }

    // Optimized Lenis configuration for desktop only
    const lenis = new Lenis({
      // Reduced duration for snappier feel and better performance
      duration: 1.0,
      // Smoother easing curve
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      gestureOrientation: "vertical",
      smoothWheel: true,
      wheelMultiplier: 1,
      infinite: false,
      autoResize: true,
    });

    // Optimized RAF loop with cleanup
    let rafId: number;

    function raf(time: number) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }

    rafId = requestAnimationFrame(raf);

    // Handle resize events for responsive behavior
    const handleResize = () => {
      const nowMobile = checkMobile();
      if (nowMobile !== mobile) {
        setIsMobile(nowMobile);
      }
    };

    window.addEventListener("resize", handleResize, { passive: true });

    // Cleanup
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", handleResize);
      lenis.destroy();
    };
  }, []);

  return <>{children}</>;
};

export default SmoothScrollbar;